import { useTranslation } from "react-i18next";
import { Volume2, VolumeX } from "lucide-react";

import { useScreenAudioShare } from "../../application/useScreenAudioShare";

/**
 * The control for sharing a tab's sound on its own.
 *
 * It sits beside screen sharing rather than inside it because the two are
 * different acts: one shows the patient what is on the therapist's screen,
 * the other only lets them hear it while both faces stay up. Both moved out
 * of the bar's front row — a session goes by without either being touched.
 *
 * The failure it has to explain is the browser's, not ours — the tab picker
 * hides its audio checkbox behind a tab that is easy to miss — so the message
 * says what to do rather than that something went wrong, and is a live region
 * because it arrives after the picker has already closed.
 */
export default function MeetingAudioShare() {
    const { t } = useTranslation();
    const { sharing, pending, error, toggle } = useScreenAudioShare();

    const Icon = sharing ? VolumeX : Volume2;

    return (
        <>
            <button
                type="button"
                onClick={toggle}
                aria-pressed={sharing}
                disabled={pending}
                className={`room-menu-item ${sharing ? "bg-room-active" : ""} disabled:opacity-50`}
                data-testid="share-audio"
            >
                <Icon size={16} aria-hidden="true" />
                <span>
                    {t(
                        sharing
                            ? "meetingControls.shareAudioStop"
                            : "meetingControls.shareAudio"
                    )}
                </span>
            </button>

            {error && (
                <p
                    role="alert"
                    className="px-2 pb-1 text-xs font-medium text-room-live"
                    data-testid="share-audio-error"
                >
                    {t(
                        error === "noAudio"
                            ? "meetingControls.shareAudioNoTrack"
                            : "meetingControls.shareAudioFailed"
                    )}
                </p>
            )}
        </>
    );
}
